// Global helper to update checkout button state based on cart total
window.updateCheckoutButtonState = function (cartTotal) {
  const checkoutButton = document.getElementById("CartDrawer-Checkout");
  if (checkoutButton) {
    if (cartTotal === 0 || cartTotal === "0") {
      checkoutButton.disabled = true;
      checkoutButton.classList.add("checkout-disabled");
    } else {
      checkoutButton.disabled = false;
      checkoutButton.classList.remove("checkout-disabled");
    }
  }
};

// Global helper for cart drawer close - define it at the top level before any other code
window.closeCartDrawer = function () {
  // First try to find a cart-drawer element
  const cartDrawer = document.querySelector("cart-drawer");
  if (cartDrawer) {
    // Remove active class to close drawer
    cartDrawer.classList.remove("active");
    document.body.classList.remove("overflow-hidden");
  } else {
    // Fallback: try to find any drawer element
    const drawer = document.querySelector(".drawer");
    if (drawer) {
      drawer.classList.remove("active", "animate");
      document.body.classList.remove("overflow-hidden");
    }
  }
};

// Global helper to re-attach cart drawer close button listeners
window.reattachCartDrawerListeners = function () {
  const cartDrawer = document.querySelector("cart-drawer");
  if (
    cartDrawer &&
    typeof cartDrawer.attachCloseButtonListeners === "function"
  ) {
    cartDrawer.attachCloseButtonListeners();
  }
};

// Global helper to ensure cart drawer header is visible
window.ensureCartDrawerHeaderVisible = function () {
  const cartDrawer = document.querySelector("cart-drawer");
  if (cartDrawer && !cartDrawer.classList.contains("is-empty")) {
    const drawerHeader = cartDrawer.querySelector(".drawer__header");
    if (drawerHeader) {
      drawerHeader.style.display = "flex";
      drawerHeader.style.visibility = "visible";
    }
  }
};

// --- Global Function to Handle Cart Icon Click ---
// Needs to be global because it's called by inline onclick
window.handleCartIconClick = function (event) {
  console.log("handleCartIconClick called");
  // Ensure preventDefault is called first
  if (event && typeof event.preventDefault === "function") {
    event.preventDefault();
  }
  if (event && typeof event.stopPropagation === "function") {
    event.stopPropagation();
  }

  const cartDrawerElement = document.querySelector("cart-drawer");
  // Use event.currentTarget to get the element the handler is attached to (the <a> tag)
  const triggeringElement = event.currentTarget;

  if (cartDrawerElement && typeof cartDrawerElement.open === "function") {
    console.log(
      "Found cart drawer, calling open(). Trigger:",
      triggeringElement,
    );
    cartDrawerElement.open(triggeringElement);
  } else if (!cartDrawerElement) {
    console.error("handleCartIconClick: Could not find <cart-drawer> element!");
  } else {
    console.error(
      "handleCartIconClick: Found <cart-drawer> but open() method is missing!",
    );
  }
  return false; // Explicitly return false for inline handler
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.activeElement = null;
    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close(),
    );
    // Capture-phase so the X still works after quick-add replaces #CartDrawer innerHTML
    // (that swap drops the old button nodes and their click listeners).
    this.addEventListener(
      "click",
      (e) => {
        if (!e.target.closest(".drawer__close")) return;
        e.preventDefault();
        this.close();
      },
      true,
    );
    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) {
      overlay.addEventListener("click", this.close.bind(this));
    }
    // Also listen for clicks on the drawer element itself (but not on drawer__inner)
    this.addEventListener("click", (e) => {
      // Only close if clicking on the overlay or the drawer background, not on drawer__inner
      const drawerInner = this.querySelector(".drawer__inner");
      if (drawerInner && drawerInner.contains(e.target)) {
        // Don't close if clicking inside the drawer content
        return;
      }
      // Close if clicking on the overlay, the drawer background, or the CartDrawer container
      if (
        e.target === this ||
        e.target.classList.contains("cart-drawer__overlay") ||
        e.target.id === "CartDrawer"
      ) {
        this.close();
      }
    });

    // Prevent clicks inside drawer__inner from bubbling up
    const drawerInner = this.querySelector(".drawer__inner");
    if (drawerInner) {
      drawerInner.addEventListener("click", (e) => {
        e.stopPropagation();
      });
    }
    this.attachCloseButtonListeners();
  }

  connectedCallback() {
    console.log("CartDrawer connected");
    // Optional: Set initial ARIA state on icon if drawer is initially closed
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (cartLink && !this.classList.contains("active")) {
      cartLink.setAttribute("aria-expanded", "false");
    }
  }

  open(triggeredBy) {
    console.log("CartDrawer.open", triggeredBy ? `by ${triggeredBy.id}` : "");
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
      triggeredBy.setAttribute("aria-expanded", "true");
    }
    if (this.classList.contains("is-empty")) {
      const cartUrl = (window.routes && window.routes.cart_url) || "/cart";
      fetch(`${cartUrl}?sections=cart-drawer,cart-icon-bubble`)
        .then((resp) => resp.json())
        .then((sections) => {
          this.renderContents({ sections });
        })
        .catch(() => {});
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
      this.setSummaryAccessibility(cartDrawerNote);
    }
    setTimeout(() => {
      this.classList.add("animate", "active");
    }, 0);
    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        if (containerToTrapFocusOn && focusElement)
          trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );
    document.body.classList.add("overflow-hidden");
  }

  close() {
    console.log("CartDrawer.close");
    this.classList.remove("active", "animate");
    if (this.activeElement) {
      this.activeElement.setAttribute("aria-expanded", "false");
      // Check if focus is needed/possible
      // if (typeof this.activeElement.focus === 'function') {
      //    this.activeElement.focus();
      // }
    }
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");
    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id,
      );
    }
    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open"),
      );
    });
    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    // This function might be called by cart updates.
    // Ensure it re-renders necessary parts without breaking event handlers.
    // It likely relies on getSectionsToRender.
    console.log(
      "CartDrawer renderContents (ensure icon bubble updates don't break inline onclick)",
    );
    // Keep empty state in sync with the server section — never force-clear is-empty.
    // Opening an empty cart used to call this and always remove is-empty, which hid
    // .drawer__inner-empty via CSS (cart-drawer:not(.is-empty) .drawer__inner-empty).
    const sectionHtml =
      parsedState.sections && parsedState.sections["cart-drawer"];
    let isEmpty = this.classList.contains("is-empty");
    if (sectionHtml) {
      const remoteDoc = new DOMParser().parseFromString(
        sectionHtml,
        "text/html",
      );
      const remoteDrawer = remoteDoc.querySelector("cart-drawer");
      isEmpty = remoteDrawer
        ? remoteDrawer.classList.contains("is-empty")
        : Boolean(remoteDoc.querySelector(".drawer__inner-empty"));
    }
    this.classList.toggle("is-empty", isEmpty);
    const drawerItems = this.querySelector("cart-drawer-items");
    if (drawerItems) drawerItems.classList.toggle("is-empty", isEmpty);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      // Careful: Replacing innerHTML might wipe handlers if not careful
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    // Re-attach close button listeners after content update
    this.attachCloseButtonListeners();

    // If open() isn't called elsewhere after render, might need it here?
    // this.open();
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    // Ensure this still includes 'cart-icon-bubble' if cart updates should refresh it
    return [
      { id: "cart-drawer", selector: "#CartDrawer" },
      { id: "cart-icon-bubble" },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeDrawer() {
    console.log("CartDrawer.initializeDrawer called");
  }

  // Method to attach close button event listeners
  attachCloseButtonListeners() {
    this.querySelectorAll(".drawer__close").forEach((button) => {
      // Remove potential inline onclick from theme editor additions
      if (button.hasAttribute("onclick")) button.removeAttribute("onclick");
      // Remove existing listeners to prevent duplicates
      button.removeEventListener("click", this.closeButtonHandler);
      // Add new listener
      button.addEventListener("click", this.closeButtonHandler.bind(this));
    });
  }

  // Separate handler method for close button clicks
  closeButtonHandler(e) {
    e.preventDefault();
    this.close();
  }
}
customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    // This is crucial for updates triggered by CartItems (like quantity changes)
    return [
      { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    console.log("CartDrawerItems updateQuantity called with:", {
      line,
      quantity,
      name,
      variantId,
    });

    // Store current scroll position
    const cartDrawerItems = this.closest("cart-drawer-items") || this;
    const currentScrollTop = cartDrawerItems.scrollTop || 0;

    this.enableLoading(line);

    // Extract the variant ID from the line string (format: variantId:hash)
    const id = line.includes(":") ? line.split(":")[0] : line;

    const body = JSON.stringify({
      id, // Use id (variant ID) instead of line
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0,
          );

        // Update content while preserving scroll position
        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);

          if (elementToReplace) {
            // Store scroll position before update
            const scrollContainer =
              elementToReplace.closest("cart-drawer-items") || elementToReplace;
            const scrollTop = scrollContainer.scrollTop || 0;

            // Update content
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector,
            );

            // Restore scroll position after content update
            requestAnimationFrame(() => {
              if (scrollContainer.scrollTo) {
                scrollContainer.scrollTo(0, scrollTop);
              } else {
                scrollContainer.scrollTop = scrollTop;
              }
            });
          }
        });

        // Re-attach close button listeners after content update
        if (typeof window.reattachCartDrawerListeners === "function") {
          window.reattachCartDrawerListeners();
        }

        // Continue with the rest of the parent logic...
        const currentQuantityElement =
          document.getElementById(`Quantity-${id}`) ||
          document.getElementById(`Drawer-quantity-${id}`);

        const serverItem = parsedState.items.find(
          (item) => item.variant_id.toString() === id,
        );
        const updatedValue = serverItem?.quantity ?? 0;

        if (!currentQuantityElement && updatedValue > 0) {
          console.warn(
            "Cart item re-appeared unexpectedly after update attempt.",
          );
          console.warn(
            `Variant ID: ${id}, Expected Element IDs: Quantity-${id} or Drawer-quantity-${id}`,
          );
        } else if (
          currentQuantityElement &&
          parseInt(currentQuantityElement.value) !== updatedValue
        ) {
          console.log(
            `Quantity mismatch for ${line}. Optimistic: ${currentQuantityElement.value}, Server: ${updatedValue}. Reverting.`,
          );
          currentQuantityElement.value = updatedValue;

          let message = window.cartStrings.quantityError.replace(
            "[quantity]",
            updatedValue,
          );
          this.updateLiveRegions(line, updatedValue > 0 ? message : "");
        } else {
          this.updateLiveRegions(line, "");
        }

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`),
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a"),
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name"),
          );
        }

        // Update checkout button state based on new cart total
        if (typeof window.updateCheckoutButtonState === "function") {
          window.updateCheckoutButtonState(parsedState.total_price);
        }

        // Check if free gifts should be removed (no longer qualifying)
        if (typeof window.checkAndRemoveFreeGifts === "function") {
          window.checkAndRemoveFreeGifts(parsedState);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });
      })
      .catch((error) => {
        console.error("Error updating cart:", error);

        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        if (quantityElement && quantityElement.getAttribute("value")) {
          quantityElement.value = quantityElement.getAttribute("value");
        }

        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        if (errors) {
          errors.textContent = window.cartStrings.error;
        }
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  // Override enableLoading to work with cart drawer structure
  enableLoading(line) {
    console.log("CartDrawerItems enableLoading called with line:", line);

    // Find the cart item by data-key attribute instead of ID
    const cartItem = this.querySelector(`[data-key="${line}"]`);
    if (cartItem) {
      const loadingSpinner = cartItem.querySelector(".loading__spinner");
      if (loadingSpinner) {
        loadingSpinner.classList.remove("hidden");
        console.log("Loading spinner shown for cart item:", line);
      } else {
        console.warn("Loading spinner not found in cart item:", line);
      }

      // Add loading class to the cart item for visual feedback
      cartItem.classList.add("cart-item--updating");
    } else {
      console.warn("Cart item not found for line:", line);
    }

    // Blur active element and update status
    document.activeElement.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute("aria-hidden", false);
    }
  }

  // Override disableLoading to work with cart drawer structure
  disableLoading(line) {
    console.log("CartDrawerItems disableLoading called with line:", line);

    // Find the cart item by data-key attribute instead of ID
    const cartItem = this.querySelector(`[data-key="${line}"]`);
    if (cartItem) {
      const loadingSpinner = cartItem.querySelector(".loading__spinner");
      if (loadingSpinner) {
        loadingSpinner.classList.add("hidden");
        console.log("Loading spinner hidden for cart item:", line);
      }

      // Remove loading class from the cart item
      cartItem.classList.remove("cart-item--updating");
    } else {
      console.warn("Cart item not found for line:", line);
    }
  }
}
customElements.define("cart-drawer-items", CartDrawerItems);

// --- Minimal DOMContentLoaded ---
// Keep essential setup like external close buttons if needed, but primary
// icon handling is now via inline onclick calling the global function.
document.addEventListener("DOMContentLoaded", function () {
  // External close buttons
  document.querySelectorAll("button.drawer__close").forEach((button) => {
    if (!button.closest("cart-drawer")) {
      if (!button.dataset.globalCloseListenerAttached) {
        // Remove potentially conflicting inline onclicks
        if (button.hasAttribute("onclick")) button.removeAttribute("onclick");
        button.addEventListener("click", function (event) {
          event.preventDefault();
          if (typeof window.closeCartDrawer === "function") {
            window.closeCartDrawer();
          } else {
            console.error("window.closeCartDrawer function not found!");
          }
        });
        button.dataset.globalCloseListenerAttached = "true";
      }
    }
  });

  // Fallback cart remove button logic (Consider if this should solely be in cart.js)
  document.querySelectorAll("cart-remove-button").forEach((button) => {
    if (!button.hasAttribute("initialized")) {
      button.setAttribute("initialized", "true");
      button.addEventListener("click", function (event) {
        event.preventDefault();
        const cartItems =
          this.closest("cart-items") || this.closest("cart-drawer-items");
        if (cartItems && typeof cartItems.updateQuantity === "function") {
          cartItems.updateQuantity(this.dataset.index, 0);
        } else {
          console.warn(
            "CartItems/updateQuantity not found for remove button fallback.",
          );
          // Add minimal fetch fallback if necessary, but ideally handled by cart.js/cart-items.js
          // Minimal example:
          // fetch(`/cart/change.js?line=${this.dataset.key}&quantity=0`, { method: 'POST' })
          //  .then(() => { /* Maybe trigger a cart update event? */ });
        }
      });
    }
  });
});
